import { useEffect, useState, useCallback } from "react";
import { Truck, XCircle, Cpu } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import { ORDER_STATUS_LABEL, orderStatusBadgeClass, isOrderInProgress } from "@/lib/orderStatus";
import PulseDot from "@/components/common/PulseDot";

const STEPS = [
  ["pending", "Requested"],
  ["accepted", "Accepted"],
  ["making", "Making"],
  ["delivery", "Delivery"],
  ["delivered", "Delivered"],
];

function Tracker({ status }) {
  if (status === "rejected") {
    return (
      <div className="flex items-center gap-2 text-kl-inactive" data-testid="order-tracker-rejected">
        <XCircle size={16} /> <span className="font-display text-sm">Rejected</span>
      </div>
    );
  }
  const idx = STEPS.findIndex(([k]) => k === status);
  return (
    <div className="flex items-center" data-testid="order-tracker">
      {STEPS.map(([key, label], i) => {
        // The current step pulses amber while it's actively being worked on
        // (anything short of "delivered") — done steps and the final
        // delivered state stay a plain solid green, so the pulse always
        // means "this is the one moving right now".
        const isCurrent = i === idx && status !== "delivered";
        const done = i < idx || status === "delivered";
        return (
          <div key={key} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5 shrink-0">
              {isCurrent ? (
                <PulseDot size="h-3 w-3" />
              ) : (
                <span className={`w-3 h-3 rounded-full border-2 ${i <= idx ? (done ? "bg-kl-green border-kl-green" : "bg-white border-kl-lineLight") : "bg-white border-kl-lineLight"}`} />
              )}
              <span className={`kl-label ${isCurrent ? "text-amber-600" : i <= idx ? "text-kl-black" : "text-kl-mutedLight"}`}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <span className={`flex-1 h-0.5 mx-1 ${i < idx ? "bg-kl-green" : "bg-kl-lineLight"}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function TrackOrders() {
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => api.get("/package-requests").then((r) => setOrders(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const current = orders.find((o) => o.id === selectedId) || orders[0] || null;
  const p = current?.package;

  return (
    <div className="h-full grid grid-cols-[360px_1fr]" data-testid="track-orders-page">
      <aside className="border-r border-kl-line flex flex-col bg-kl-black">
        <div className="p-6 border-b border-kl-line">
          <p className="kl-label-dark">Hardware</p>
          <h1 className="font-display font-light text-2xl mt-1">Track order <span className="font-mono text-sm text-kl-muted">{orders.length}</span></h1>
          <p className="text-xs text-kl-muted mt-2 leading-relaxed">Every package you've requested for a client's system, and where it stands with KyraLink.</p>
        </div>
        <ul className="flex-1 overflow-y-auto kl-scroll">
          {orders.length === 0 && <li className="p-6 text-sm text-kl-muted" data-testid="orders-empty">No requests yet — request a package from a proposal to see it here.</li>}
          {orders.map((o) => (
            <li key={o.id} className={`border-b border-kl-line ${current?.id === o.id ? "bg-kl-surface" : "hover:bg-kl-surface"} transition-colors duration-150`} data-testid={`order-row-${o.id}`}>
              <button onClick={() => setSelectedId(o.id)} className="w-full text-left p-4 kl-focus">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display text-sm truncate">{o.client_name}</span>
                  <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${orderStatusBadgeClass(o.status)}`}>
                    {isOrderInProgress(o.status) && <PulseDot size="h-1.5 w-1.5" />}
                    {ORDER_STATUS_LABEL[o.status] || o.status}
                  </span>
                </div>
                <div className="font-mono text-[11px] text-kl-muted mt-1 truncate">{o.serial}</div>
                <div className="text-[11px] text-kl-muted mt-1">requested {fmt.date(o.requested_at)}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="bg-white text-kl-black overflow-y-auto kl-scroll-light">
        {current ? (
          <div className="max-w-3xl mx-auto p-8 animate-rise" data-testid="order-detail">
            <p className="kl-label">{current.serial} · requested {fmt.time(current.requested_at)} by {current.requested_by}</p>
            <h2 className="font-display font-light text-3xl mt-1">{current.client_name}</h2>

            <div className="kl-panel p-6 mt-6">
              <Tracker status={current.status} />
              {current.status === "rejected" && current.reject_reason && (
                <p className="text-sm text-kl-inactive mt-4">Reason: {current.reject_reason}</p>
              )}
            </div>

            {p && (
              <div className="kl-panel-dark p-5 mt-4">
                <p className="kl-label-dark flex items-center gap-1.5"><Cpu size={12} /> Requested package</p>
                <div className="mt-3 space-y-2 font-mono text-sm">
                  <div className="flex justify-between"><span className="text-kl-muted">Panel</span><span>{p.panel.qty} × {p.panel.brand} {p.panel.model} ({p.panel.rating} {p.panel.unit})</span></div>
                  <div className="flex justify-between"><span className="text-kl-muted">Inverter</span><span>{p.inverter.qty} × {p.inverter.brand} {p.inverter.model} ({p.inverter.rating} {p.inverter.unit})</span></div>
                  <div className="flex justify-between"><span className="text-kl-muted">Battery</span><span>{p.battery.qty} × {p.battery.brand} {p.battery.model} ({p.battery.rating} {p.battery.unit})</span></div>
                  <div className="flex justify-between border-t border-kl-line pt-2 mt-2"><span className="text-kl-muted">System cost</span><span className="text-kl-green">{fmt.money(p.system_cost, p.currency)}</span></div>
                </div>
              </div>
            )}

            <div className="kl-panel p-5 mt-4">
              <p className="kl-label flex items-center gap-1.5"><Truck size={12} /> Status history</p>
              <ul className="mt-3 space-y-2">
                {current.status_log?.slice().reverse().map((e, i) => (
                  <li key={i} className="text-xs"><span className="font-display capitalize">{e.status}</span><span className="text-kl-mutedLight"> · {fmt.time(e.at)} · by {e.by}</span>{e.note && <div className="text-kl-mutedLight mt-0.5">{e.note}</div>}</li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center p-10" data-testid="orders-empty-state">
            <Truck size={28} className="text-kl-mutedLight" />
            <h2 className="font-display text-2xl mt-4">No orders yet</h2>
            <p className="text-sm text-kl-mutedLight mt-1 max-w-sm">Request a package from a proposal (Reports or History) to start tracking it here.</p>
          </div>
        )}
      </section>
    </div>
  );
}
